import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ArrowUpDown,
  LayoutGrid,
  Grid2X2,
  Grid3X3,
  Check,
  Sparkles,
} from "lucide-react";
import { AnnouncementBar } from "../components/AnnouncementBar";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { Eyebrow, SerifH } from "../components/shared";
import { PRODUCTS } from "../data/content";

const FILTERS = [
  { key: "all",   label: "All jellabas" },
  { key: "new",   label: "New arrivals",  test: (p) => p.tag === "NEW" || p.tag === "NEW DROP" },
  { key: "under", label: "Under €200",    test: (p) => parseInt(p.price.replace(/\D/g, ""), 10) < 200 },
  { key: "low",   label: "Low stock",     test: (p) => p.tag === "Only 3 left" },
];

const SORTS = [
  { key: "featured", label: "Featured" },
  { key: "newest",   label: "Newest first" },
  { key: "price-asc",  label: "Price · low to high" },
  { key: "price-desc", label: "Price · high to low" },
];

const priceNum = (p) => parseInt(p.price.replace(/\D/g, ""), 10);
const newestRank = (p) =>
  p.tag === "NEW DROP" ? 0 : p.tag === "NEW" ? 1 : 2;

function SortMenu({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const active = SORTS.find((s) => s.key === value) || SORTS[0];
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="collection-sort"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase border border-[#0C111C]/20 hover:border-[#0C182E] px-4 py-2.5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <ArrowUpDown size={14} />
        Sort · {active.label}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            data-testid="collection-sort-menu"
            className="absolute right-0 z-20 mt-2 min-w-[220px] bg-[#F4EDE2] border border-[#0C111C]/10 shadow-[0_20px_50px_rgba(12,17,28,0.14)] py-1.5"
          >
            {SORTS.map((s) => (
              <li key={s.key}>
                <button
                  type="button"
                  data-testid={`collection-sort-${s.key}`}
                  onClick={() => { onChange(s.key); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                    value === s.key ? "bg-[#C8A96A]/15" : "hover:bg-[#C8A96A]/10"
                  }`}
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <span className="flex-1">{s.label}</span>
                  {value === s.key && <Check size={14} className="text-[#C8A96A]" />}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

function DensityToggle({ value, onChange }) {
  const options = [
    { key: 2, Icon: Grid2X2 },
    { key: 3, Icon: LayoutGrid },
    { key: 4, Icon: Grid3X3 },
  ];
  return (
    <div
      className="hidden md:inline-flex border border-[#0C111C]/20"
      data-testid="collection-density"
    >
      {options.map(({ key, Icon }) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            aria-label={`${key} columns`}
            data-testid={`density-${key}`}
            onClick={() => onChange(key)}
            className={`px-3 h-[42px] transition-colors ${
              active ? "bg-[#0C182E] text-[#F4EDE2]" : "text-[#0C111C] hover:bg-[#0C111C]/5"
            }`}
          >
            <Icon size={16} />
          </button>
        );
      })}
    </div>
  );
}

function ProductTile({ p, big }) {
  return (
    <motion.article
      data-testid={`collection-tile-${p.slug}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="group"
    >
      <Link to={`/product/${p.slug}`} className="block">
        <div className="relative overflow-hidden bg-[#EDE2CE] aspect-[558/1000]">
          <img
            src={p.image}
            alt={`${p.name} — ${p.print}`}
            loading="lazy"
            className={`absolute inset-0 h-full w-full object-cover transition-all duration-[900ms] ease-out ${
              p.hoverImage ? "group-hover:opacity-0" : "group-hover:scale-[1.03]"
            }`}
          />
          {p.hoverImage && (
            <img
              src={p.hoverImage}
              alt={`${p.name} — back view`}
              loading="lazy"
              data-testid={`collection-tile-back-${p.slug}`}
              className="absolute inset-0 h-full w-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-[900ms] ease-out"
            />
          )}
          {p.tag === "NEW DROP" && (
            <div
              className="pointer-events-none absolute top-0 left-0 overflow-hidden"
              style={{ width: 140, height: 140 }}
              aria-label="New drop"
            >
              <div
                className="absolute"
                style={{
                  top: 26,
                  left: -42,
                  width: 180,
                  transform: "rotate(-45deg)",
                  background: "linear-gradient(90deg, #C8A96A 0%, #E9CE95 50%, #C8A96A 100%)",
                  color: "#0C111C",
                  textAlign: "center",
                  padding: "6px 0",
                  fontSize: 10,
                  letterSpacing: "0.28em",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  boxShadow: "0 4px 12px rgba(12,17,28,0.18)",
                }}
              >
                NEW DROP
              </div>
            </div>
          )}
          {p.tag && p.tag !== "NEW DROP" && (
            <span
              className={`absolute top-4 left-4 rounded-full px-3 py-1 text-[10px] tracking-[0.2em] uppercase ${
                p.tag === "NEW" ? "bg-[#C8A96A] text-[#0C182E]" : "bg-[#B95841] text-[#F4EDE2]"
              }`}
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {p.tag}
            </span>
          )}
        </div>
        <div className="mt-5 flex items-baseline justify-between">
          <div>
            <p className={big ? "text-xl md:text-2xl" : "text-lg"} style={{ fontFamily: "Fraunces, serif" }}>
              {p.name}
            </p>
            <p
              className="text-sm text-[#4A3626]/80"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {p.print}
            </p>
          </div>
          <p className={big ? "text-xl md:text-2xl text-[#4A3626]" : "text-lg text-[#4A3626]"}
             style={{ fontFamily: "Fraunces, serif" }}>
            {p.price}
          </p>
        </div>
      </Link>
    </motion.article>
  );
}

export function CollectionPage() {
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("featured");
  const [density, setDensity] = useState(3);

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    return f?.test ? PRODUCTS.filter(f.test) : PRODUCTS.slice();
  }, [filter]);

  const sorted = useMemo(() => {
    const list = filtered.slice();
    switch (sort) {
      case "price-asc":  return list.sort((a, b) => priceNum(a) - priceNum(b));
      case "price-desc": return list.sort((a, b) => priceNum(b) - priceNum(a));
      case "newest":     return list.sort((a, b) => newestRank(a) - newestRank(b));
      default:           return list;
    }
  }, [filtered, sort]);

  const total = PRODUCTS.length;
  const gridColsClass = {
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
  }[density];

  return (
    <div className="bg-[#F4EDE2] text-[#0C111C] min-h-screen" data-testid="collection-page">
      <AnnouncementBar />
      <Nav />

      {/* Editorial header */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 pt-14 md:pt-24 pb-10 md:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-10 items-end">
            <div>
              <Eyebrow>SS26 · The Collection</Eyebrow>
              <SerifH className="mt-4" size="clamp(44px, 6.5vw, 96px)">
                Sixteen jellabas.<br />
                One quiet obsession.
              </SerifH>
            </div>
            <p
              className="text-base md:text-lg text-[#3A3A3E] max-w-lg lg:justify-self-end"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Each piece cut and hand-finished in Fes. Limited runs, no two prints
              alike. Machine-washable premium viscose. Real gold-thread sfifa.
              Pick the one that finds you.
            </p>
          </div>
        </div>

        {/* Highlight banner */}
        <div
          className="bg-[#0C182E] text-[#C8A96A] text-[11px] tracking-[0.24em] uppercase"
          data-testid="collection-highlight"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 py-3 flex items-center justify-center gap-3 flex-wrap">
            <Sparkles size={14} className="text-[#C8A96A]" />
            Four fresh drops just added — Chefchaouen, Essaouira, Meknes, Ourika
          </div>
        </div>
      </section>

      {/* Sticky filter + sort bar */}
      <section
        className="sticky top-[65px] z-20 bg-[#F4EDE2]/95 backdrop-blur-md border-b border-[#0C111C]/10"
        data-testid="collection-toolbar"
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 py-4 flex items-center justify-between gap-4">
          {/* Filter chips */}
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            {FILTERS.map((f) => {
              const count =
                f.key === "all" ? total : PRODUCTS.filter(f.test).length;
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  data-testid={`filter-${f.key}`}
                  onClick={() => setFilter(f.key)}
                  className={`inline-flex items-center gap-2 shrink-0 rounded-full px-4 py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors ${
                    active
                      ? "border-[#0C182E] bg-[#0C182E] text-[#F4EDE2]"
                      : "border-[#0C111C]/20 text-[#0C111C] hover:border-[#0C182E]"
                  }`}
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  {f.label}
                  <span
                    className={`text-[10px] tracking-[0.15em] ${
                      active ? "text-[#C8A96A]" : "text-[#0C111C]/50"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <DensityToggle value={density} onChange={setDensity} />
            <SortMenu value={sort} onChange={setSort} />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section
        className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 py-12 md:py-16"
        data-testid="collection-grid-section"
      >
        <div
          className="flex items-baseline justify-between mb-8 text-[11px] tracking-[0.22em] uppercase"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <span data-testid="collection-count">
            Showing {sorted.length} of {total}
          </span>
          <button
            type="button"
            onClick={() => { setFilter("all"); setSort("featured"); }}
            data-testid="collection-reset"
            className="text-[#0C111C]/60 hover:text-[#C8A96A] underline underline-offset-4 decoration-[#C8A96A]"
          >
            Reset filters
          </button>
        </div>

        {sorted.length === 0 ? (
          <div
            className="py-24 text-center"
            data-testid="collection-empty"
          >
            <SerifH size="clamp(28px, 3vw, 44px)">Nothing matches — yet.</SerifH>
            <p
              className="mt-4 text-[#3A3A3E]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Try a different filter, or reset to see all twelve.
            </p>
            <button
              type="button"
              onClick={() => setFilter("all")}
              className="mt-8 inline-flex items-center gap-2 bg-[#0C182E] text-[#F4EDE2] px-6 h-12 text-sm tracking-[0.22em] uppercase"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Show all jellabas
            </button>
          </div>
        ) : (
          <motion.div
            key={`${filter}-${sort}-${density}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className={`grid gap-x-6 gap-y-14 md:gap-x-8 ${gridColsClass}`}
          >
            {sorted.map((p) => (
              <ProductTile key={p.slug} p={p} big={density === 2} />
            ))}
          </motion.div>
        )}
      </section>

      {/* Editorial CTA strip */}
      <section
        className="bg-[#0C182E] text-[#F4EDE2] py-24 md:py-32"
        data-testid="collection-cta"
      >
        <div className="mx-auto max-w-[900px] px-6 text-center">
          <Eyebrow className="text-[#C8A96A]">Not sure where to start?</Eyebrow>
          <SerifH className="mt-4 text-[#F4EDE2]" size="clamp(30px, 3.4vw, 52px)">
            Message us. A real human replies in under an hour.
          </SerifH>
          <p
            className="mt-6 text-[#F4EDE2]/70 max-w-lg mx-auto"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Tell us your height, build, and how you'd wear it. We'll come back
            with two or three picks — and honest answers on fit.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 bg-[#C8A96A] hover:bg-[#E9CE95] text-[#0C182E] px-8 h-14 text-sm tracking-[0.22em] uppercase transition-colors"
            style={{ fontFamily: "Inter, sans-serif" }}
            data-testid="collection-cta-contact"
          >
            Get personal picks →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
